import React, { useState } from 'react';
import { BookOpen, ArrowRight, Star, Camera, MessageSquare, TrendingUp, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const guides = [
  {
    icon: <Camera className="w-6 h-6 text-yellow-500" />,
    title: 'Writing a Great Profile',
    desc: 'Learn how to write a bio, choose photos, and set your pricing to attract more customers.',
    tag: 'Getting Started',
    content: [
      { heading: 'Your Profile Photo', body: 'Use a clear, friendly headshot with good lighting. Avoid group photos or blurry images. Customers are more likely to book someone they can see clearly.' },
      { heading: 'Write a Compelling Bio', body: 'Start with your experience and speciality. For example: "I\'ve been a professional electrician for 8 years, specialising in residential wiring and safety inspections across Colombo." Be specific about what you do and where.' },
      { heading: 'Set Competitive Pricing', body: 'Research what others in your category charge on HIVE. Start slightly lower to build reviews, then raise your rates as you get more bookings and social proof.' },
      { heading: 'List Your Skills Clearly', body: 'Don\'t just say "cleaning" — say "deep house cleaning, move-in/move-out cleaning, office cleaning." The more specific, the more likely you are to show up in the right searches.' },
      { heading: 'Keep It Updated', body: 'Update your availability and services regularly. An outdated profile with old pricing can cost you bookings.' },
    ],
  },
  {
    icon: <Star className="w-6 h-6 text-yellow-500" />,
    title: 'Getting Your First Review',
    desc: 'Tips for delivering a great first job and encouraging customers to leave feedback.',
    tag: 'Growth',
    content: [
      { heading: 'Over-Deliver on the First Job', body: 'Your first review sets the tone for everything. Arrive on time, be polite, do a thorough job, and leave the place cleaner than you found it. First impressions stick.' },
      { heading: 'Communicate Before You Arrive', body: 'Send a message the day before confirming the time and what you\'ll need. Customers appreciate providers who communicate proactively — it builds trust before you even show up.' },
      { heading: 'Ask Politely at the End', body: 'After completing the job, say something like: "I really appreciate your business. If you\'re happy with the work, a quick review on HIVE would mean a lot to me." Most satisfied customers are happy to leave one if asked.' },
      { heading: 'Follow Up', body: 'Send a short message after the job: "Thanks again for booking! I hope everything looks great. Feel free to reach out if you need anything." This keeps you top of mind for repeat bookings and reviews.' },
    ],
  },
  {
    icon: <MessageSquare className="w-6 h-6 text-yellow-500" />,
    title: 'Responding to Customers',
    desc: 'Best practices for communicating quickly, professionally and closing bookings.',
    tag: 'Communication',
    content: [
      { heading: 'Respond Within 1 Hour', body: 'Speed matters. Customers often message multiple providers at once. The first to respond with a clear, professional message usually gets the booking.' },
      { heading: 'Be Clear and Specific', body: 'Don\'t just say "yes I can do it." Say: "Yes, I can come this Saturday at 10am. For a 3-bedroom house, it\'ll take about 3 hours and cost LKR 4,500. Does that work?" Specifics close bookings.' },
      { heading: 'Handle Negotiations Professionally', body: 'If a customer tries to lower your price, don\'t just say no. Explain your value: "I use professional-grade products and have 5 years of experience. My rate reflects the quality of work." Stand firm but stay friendly.' },
      { heading: 'Confirm Every Booking in Writing', body: 'Always send a confirmation message with the date, time, price and what\'s included. This prevents misunderstandings and shows you\'re organised and professional.' },
    ],
  },
  {
    icon: <TrendingUp className="w-6 h-6 text-yellow-500" />,
    title: 'Pricing Your Services',
    desc: 'How to research competitive rates and price yourself to win jobs while staying profitable.',
    tag: 'Business',
    content: [
      { heading: 'Research the Market', body: 'Search your own service category on HIVE and see what competitors charge. Note the top-rated providers\' prices — they\'re usually a good benchmark for what customers expect to pay for quality.' },
      { heading: 'Factor in Your Costs', body: 'Don\'t forget to account for transport, supplies, tools and your time travelling to the job. Many providers underprice because they forget these hidden costs.' },
      { heading: 'Start Competitive, Scale Up', body: 'When you\'re new, price 10-15% below the average to win your first bookings and reviews. Once you have 10+ positive reviews, raise your rates to market value.' },
      { heading: 'Offer Package Deals', body: 'Consider offering bundles — for example, "Regular cleaning every 2 weeks at a 10% discount." Recurring customers are more valuable than one-time bookings.' },
      { heading: 'Be Transparent', body: 'Always give a clear quote before starting. Surprise charges after a job destroy trust and lead to bad reviews. If there\'s extra work, discuss it before doing it.' },
    ],
  },
  {
    icon: <BookOpen className="w-6 h-6 text-yellow-500" />,
    title: 'Understanding Your Dashboard',
    desc: 'A walkthrough of your provider dashboard — bookings, analytics and profile management.',
    tag: 'Platform',
    content: [
      { heading: 'The Provider Tab', body: 'Switch to the Provider tab in your dashboard to see your listed services, active jobs, completed jobs and total earnings. This is your business hub on HIVE.' },
      { heading: 'Managing Your Services', body: 'From the My Services section, you can edit your service details, update your price, change your availability status between Active and Inactive, and delete services you no longer offer.' },
      { heading: 'Tracking Bookings', body: 'All bookings appear in your dashboard with their status — Pending, Confirmed, In Progress, or Completed. Keep these updated so customers always know what\'s happening.' },
      { heading: 'Your Earnings', body: 'The Earnings stat shows your total from completed bookings. Use this to track your income over time and decide when it\'s time to raise your rates.' },
      { heading: 'Ratings & Reviews', body: 'Your average rating is shown in the dashboard. A high rating (4.5+) significantly increases how often you appear in search results. Prioritise quality on every job.' },
    ],
  },
  {
    icon: <ArrowRight className="w-6 h-6 text-yellow-500" />,
    title: 'Scaling to Pro',
    desc: 'When and how to upgrade to Pro — plus tips on maximising your HIVE presence.',
    tag: 'Growth',
    content: [
      { heading: 'When to Go Pro', body: 'Consider upgrading to Pro once you\'re consistently getting enquiries and completing bookings. The Pro badge and priority search placement will help you win more of the jobs you\'re already competing for.' },
      { heading: 'List More Services', body: 'Pro providers can list up to 10 services. If you offer multiple skills (e.g. electrical work AND AC installation), listing them separately means you show up in more searches.' },
      { heading: 'Ask for Reviews Consistently', body: 'The providers who grow fastest on HIVE are the ones who ask for a review after every single job. 20+ reviews puts you in a completely different league from providers with 2-3.' },
      { heading: 'Respond to Everything', body: 'Even if you\'re busy, respond to every enquiry — even if it\'s just to say "I\'m fully booked this week but available next week." Responsiveness builds reputation even when you can\'t take the job.' },
      { heading: 'Build Repeat Business', body: 'The most profitable providers on HIVE have regular customers. After a good job, let the customer know you\'re available for future work. A loyal base of repeat customers is more valuable than chasing new ones constantly.' },
    ],
  },
];

const tagColors = {
  'Getting Started': 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  'Growth': 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  'Communication': 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  'Business': 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  'Platform': 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
};

function GuideModal({ guide, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex items-center justify-center">
              {guide.icon}
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 dark:text-white">{guide.title}</h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${tagColors[guide.tag]}`}>{guide.tag}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {guide.content.map((section, i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">{section.heading}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>
        <div className="p-6 pt-0">
          <button onClick={onClose} className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Resources() {
  const [openGuide, setOpenGuide] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Navigation />

      {openGuide && <GuideModal guide={openGuide} onClose={() => setOpenGuide(null)} />}

      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-4">Provider Resources</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">Everything you need to succeed as a HIVE provider — guides, tips and best practices.</p>
        </div>
      </section>

      <section className="py-8 pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide, i) => (
              <div
                key={i}
                onClick={() => setOpenGuide(guide)}
                className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6 hover:border-yellow-400/50 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    {guide.icon}
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-lg ${tagColors[guide.tag]}`}>{guide.tag}</span>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors">{guide.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{guide.desc}</p>
                <div className="mt-4 flex items-center text-yellow-600 dark:text-yellow-400 text-sm font-semibold">
                  Read Guide <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-8 text-center text-white">
            <h3 className="text-xl font-black mb-2">Ready to Start Earning?</h3>
            <p className="text-yellow-100 text-sm mb-6">List your first service on HIVE today — it's free to get started.</p>
            <button
              onClick={() => window.location.href = localStorage.getItem('token') ? '/post-service' : '/auth'}
              className="px-8 py-3 bg-white text-yellow-600 rounded-xl font-bold hover:shadow-lg transition-all inline-flex items-center gap-2"
            >
              List Your Service <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}